/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import util from "../util/util.js";
import fieldManager from "../util/fields.js";

var program = {
	code: "cockywatch",
	name: "CockyWatch road survey (WA)",
	description: "<p>CockyWatch road surveys are a new citizen science initiative that aims to help us find out more about the Black-Cockatoos that call Australia’s Southwest home.</p>" +
		"<p>Surveys are easy to do. Any time you’re going for a drive that’s longer than 20 kilometres, you can do a CockyWatch survey. In addition to providing some details on your journey, you count the groups of black-cockatoos you encounter on the way. You can contribute surveys at any time of the year and repeat surveys of the same route are appreciated (particularly seasonally).</p>" +
		"<p>Please note: Chance sightings of Black-Cockatoos can’t be used as CockyWatch surveys, but can still be recorded as incidental sightings under General Birdata Surveys.</p>" +
		"<p>For more information, visit <a href='https://birdlife.org.au/projects/southwest-black-cockatoo-recovery/cockywatch'>https://birdlife.org.au/projects/southwest-black-cockatoo-recovery/cockywatch</a></p>",
	load: function() {
		return birdata.lookup().then(function(lookups) {
			program.options = lookups;
			return true;
		});
	},
	programData: function(survey) {
		var fields = program.surveyFields();

		var speciesIDs = [
			264, //Red-tailed Black-Cockatoo
			266, //Baudin's Black-Cockatoo
			794, //Carnaby's Black-Cockatoo
			841, //White-tailed Black-Cockatoo spp
			5091 // Black-Cockatoo spp
		];

		var blackCockatoos = ko.observable([]);
		var blackCockatoosWithoutCarnabyBaudin = ko.observable([]);

		birdata.speciesList().then(function(spp) {
			blackCockatoos(filterSpecies(spp, speciesIDs));
			blackCockatoosWithoutCarnabyBaudin(filterSpecies(blackCockatoos(), [264, 841, 5091]));
		});

		function filterSpecies(spp, ids) {
			return spp.filter(function(sp) { return ids.includes(sp.id); });
		}

		return {
			fields: fields,
			validations: Object.values(fields),
			speciesList: ko.computed(function() {
				return fields.observerCanDistinguishCarnabyBaudin() ? blackCockatoos() : blackCockatoosWithoutCarnabyBaudin();
			})
		};
	},
	fields: function() {
		return program.surveyFields();
	},
	surveyFields: function() {
		var fields = [
			{
				name: "startDescription",
				type: "text",
				label: "Start point description",
				validate: "required",
				tab: "location"
			},
			{
				name: "finishDescription",
				type: "text",
				label: "Finish point description",
				validate: "required",
				tab: "location"
			},
			{
				name: "surveyDistanceInKilometres",
				type: "number",
				decimalPlaces: 1,
				min: 0,
				max: 9999,
				label: "Total distance travelled",
				units: "km",
				validate: "required",
				tab: "location"
			},
			{
				name: "surveyMeans",
				type: "options",
				label: "Mode of transport",
				options: program.options.s2020_survey_means.filter(function(x) {
					return ['on_foot', 'car_or_truck', 'bicycle'].includes(x.code);
				}),
				validate: "required"
			},
			{
				name: "observerCanDistinguishCarnabyBaudin",
				type: "boolean",
				label: "Can you distinguish between Carnaby’s and Baudin’s Black-Cockatoos?",
				allowNull: true,
				validate: "required"
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	sightingFields: function(sightingModel) {
		var species = sightingModel ? sightingModel.species : null;

		// To avoid null issues
		function nestingCodeIsOneOf(/*args*/) {
			var nesting = fields.nesting();
			return Array.from(arguments).includes(nesting?.code);
		};

		var fields = [
			{
				name: "habitat",
				type: "options",
				label: "Habitat",
				options: program.options.wa_cockies_habitat,
				validate: "required"
			},
			{
				name: "activity",
				type: "options",
				label: "Activity",
				options: program.options.cockywatch_activity,
				validate: "required"
			},
			{
				name: "distanceFromRoadInMetres",
				type: "number",
				label: "Distance from road",
				min: 0,
				max: 999,
				units: "m",
				validate: "required"
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().cockywatch.fields;
		json.cockywatch = {};
		var route = survey.routeInfo();
		if(route) {
			if(route.start) {
				json.cockywatch.start_lon = route.start.lon;
				json.cockywatch.start_lat = route.start.lat;
			}
			if(route.finish) {
				json.cockywatch.finish_lon = route.finish.lon;
				json.cockywatch.finish_lat = route.finish.lat;
			}
		}
		json.all_species_recorded = false;

		for(let f of Object.values(fields)) {
			f.field.save(json.cockywatch);
		}

		if(json.survey_point && !json.survey_point.id) {
			json.survey_point.name = fields.startDescription() + " to " + fields.finishDescription();
		}
	},
	hideSurveyType: true,
	surveyTypeOptions: [],
	afterSelect: function(survey) {
		// Set survey type after program selected
		survey.type({
			code: 'fixed_route_search',
			name: 'Fixed route search'
		});
		survey.allSpeciesRecorded(false);
	},
	beforeDeselect: function(survey) {
		survey.type(null);
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.cockywatch;
		for(let f of Object.values(fields)) {
			f.field.load(data);
		}
	},
	visibleSharedSiteLayers: () => [],
	defaultAreaLayer: (layers) => null,
	updateMap: function(map, survey, areasPromise, locationViewModel) {
	},
	routeBasedEntry: true,
	existingRoutesDefinedBySharedSites: false,
	multipleSightingsPerSpecies: true,
	sightingLocations: true,
	forceSightingLocations: true,
	allSpeciesRecordedEnabled: function() { return false; },
	multipleDaysEnabled: function() { return false; },
	observerCountEnabled: function() { return true; },
	handleSurveyPointSelection: function(surveyPoint, survey) {
		var fields = survey.programData().cockywatch.fields;

		if(surveyPoint.id) {
			// If survey point is a previous cockywatch, then load start/finish, method
			birdata.surveyPoint(surveyPoint.id).then(function(sp) {
				if(sp.cockywatch) {
					program.loadFromJSON(sp, fields, survey);
					survey.locationViewModel.startPoint({ lat: sp.cockywatch.start_lat, lon: sp.cockywatch.start_lon });
					survey.locationViewModel.finishPoint({ lat: sp.cockywatch.finish_lat, lon: sp.cockywatch.finish_lon });
				}
			});
		} else {
			surveyPoint.name('Custom route'); // Note: this will be replaced on submit (see updateSurveyJSON)
		}
	}
};

export default program;
